import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, TrendingUp } from "lucide-react";

export default function ClientProgress() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    weight: "", body_fat: "", waist: "", hips: "", chest: "", photo_url: "", notes: "",
  });

  const load = () => api.get("/progress").then((r) => setItems(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form };
      ["weight", "body_fat", "waist", "hips", "chest"].forEach((k) => {
        body[k] = body[k] === "" ? null : parseFloat(body[k]);
      });
      await api.post("/progress", body);
      toast.success("Saved");
      setForm({ date: new Date().toISOString().slice(0, 10), weight: "", body_fat: "", waist: "", hips: "", chest: "", photo_url: "", notes: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8 lg:mt-0 mt-10" data-testid="client-progress">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Honest ledger</div>
          <h1 className="font-serif text-5xl mt-3">Progress</h1>
        </div>
        <button className="btn-primary" onClick={() => setOpen(!open)} data-testid="new-progress-btn"><Plus size={14}/> New entry</button>
      </header>

      {/* Wearables (placeholder) */}
      <section className="card-luxe" data-testid="wearables-block">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="overline">Wearables</p>
            <h3 className="font-display text-2xl mt-3">Connect your data</h3>
            <p className="font-soft text-base mt-3 max-w-xl" style={{ color: "var(--text-secondary)" }}>
              Sync sleep, HRV, steps, and recovery from your favorite wearable.
              Coming soon — handshake with your device the moment your DJ enables it.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["Apple Health", "Whoop", "Oura", "Garmin", "Fitbit"].map((b) => (
              <span key={b} className="font-label text-[10px] tracking-[0.22em] uppercase px-3 py-2 border" style={{ borderColor: "var(--border-strong)", color: "var(--text-tertiary)" }}>
                {b}
              </span>
            ))}
          </div>
        </div>
      </section>

      {open && (
        <form onSubmit={submit} className="card-luxe space-y-5 max-w-3xl" data-testid="progress-form">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
            <div><label className="label-luxe">Date</label><input type="date" className="input-luxe" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            <div><label className="label-luxe">Weight (lb)</label><input type="number" step="0.1" className="input-luxe" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} data-testid="progress-weight" /></div>
            <div><label className="label-luxe">Body fat %</label><input type="number" step="0.1" className="input-luxe" value={form.body_fat} onChange={(e) => setForm({ ...form, body_fat: e.target.value })} /></div>
            <div><label className="label-luxe">Waist (in)</label><input type="number" step="0.1" className="input-luxe" value={form.waist} onChange={(e) => setForm({ ...form, waist: e.target.value })} /></div>
            <div><label className="label-luxe">Hips (in)</label><input type="number" step="0.1" className="input-luxe" value={form.hips} onChange={(e) => setForm({ ...form, hips: e.target.value })} /></div>
            <div><label className="label-luxe">Chest (in)</label><input type="number" step="0.1" className="input-luxe" value={form.chest} onChange={(e) => setForm({ ...form, chest: e.target.value })} /></div>
          </div>
          <div><label className="label-luxe">Photo URL (optional)</label><input className="input-luxe" value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} /></div>
          <div><label className="label-luxe">Notes</label><textarea rows={2} className="input-luxe" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <button className="btn-primary" data-testid="progress-submit">Save</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((p) => (
          <div key={p.progress_id} className="card-luxe" data-testid={`progress-${p.progress_id}`}>
            <div className="flex items-center justify-between">
              <div className="overline">{p.date}</div>
              <TrendingUp size={14} className="text-accent" />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-4">
              {p.weight != null && <div><div className="overline">Weight</div><div className="font-mono-luxe text-xl mt-1">{p.weight}</div></div>}
              {p.body_fat != null && <div><div className="overline">BF%</div><div className="font-mono-luxe text-xl mt-1">{p.body_fat}</div></div>}
              {p.waist != null && <div><div className="overline">Waist</div><div className="font-mono-luxe text-xl mt-1">{p.waist}</div></div>}
            </div>
            {p.photo_url && <img src={p.photo_url} alt="" className="mt-4 w-full h-48 object-cover" />}
            {p.notes && <p className="text-sm text-secondary-luxe mt-3 italic">{p.notes}</p>}
          </div>
        ))}
        {items.length === 0 && <div className="text-secondary-luxe">No entries yet.</div>}
      </div>
    </div>
  );
}
